import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
	get, cloneDeep, pick, isNull, isEqual,
} from 'lodash';
import moment from 'moment';
import {
	Row, Col, FormGroup, ControlLabel, Checkbox,
} from 'react-bootstrap';
import { CalendarDatePicker, Select } from '@components/common';
import Actions, { thunks } from '@redux/payments';
import PaymentsContext from '../../paymentsContext';

class Filters extends Component {
	constructor(props) {
		super(props);

		this.datePickerSettings = {
			timeFormat : false,
			dateFormat : 'M/D/YYYY',
			inputProps : { readOnly : true },
		};

		this.invoiceTypeFilters = [
			{
				label : 'All',
				value : 'null',
			},
			{
				label : 'Estimates',
				value : true,
			},
			{
				label : 'Invoices',
				value : false,
			},
		];
	}

	shouldComponentUpdate = ({
		loading, filters, data, officerOrAgency,
	}) => {
		const lastProps = this.props;

		return (
			!isEqual(loading, lastProps.loading)
			|| !isEqual(filters, lastProps.filters)
			|| !isEqual(data, lastProps.data)
			|| !isEqual(officerOrAgency, lastProps.officerOrAgency)
		);
	}

	onFilter = (property) => (event, ...searchContactParams) => {
		const { applyCriteria, updateFilters } = this.props;
		let value = get(event, ['value'], null);

		if (property === 'officerId') value = get(searchContactParams, ['1'], null);
		if (property === 'prePay') {
			// A value of 'false' causes prepay jobs to be hidden entirely.
			// We want to either show prepay exclusively (true) or include all others (null)
			value = event.target.checked === true ? true : null;
		}

		applyCriteria([{ property, value }]);
		// clear any selected invoices
		updateFilters([{ property : 'id', value : [] }]);
	}

	changeDate = (property) => (momentValue) => {
		const { updateFilters, applyCriteria } = this.props;
		let value = cloneDeep(momentValue);

		if (moment.isMoment(value)) value = value.format('YYYY-M-D');

		if (isNull(value)) applyCriteria([{ property, value }]);

		updateFilters([{ property, value }]);
		// clear any selected invoices
		updateFilters([{ property : 'id', value : [] }]);
	}

	onDateSelected = (property) => () => {
		const { applyCriteria, filters } = this.props;
		const value = get(filters, [property], null);

		applyCriteria([{ property, value }]);
	}

	render() {
		const {
			loading : {
				loadingClients,
				loadingLocations,
			},
			filters : {
				clientId,
				locationId,
				jobStartDate,
				jobEndDate,
				invoiceStartDate,
				invoiceEndDate,
				isEstimate,
				prePay,
			},
			data    : {
				locations,
				clients,
			},
			officerOrAgency,
		} = this.props;

		const renderInvoideDateRange = (activePage) => (
			<Fragment>
				<Col md={3}>
					<FormGroup>
						<Choose>
							<When condition={(activePage === 'payment' && officerOrAgency === 'agency')}>
								<ControlLabel>Start Date</ControlLabel>
							</When>
							<Otherwise>
								<ControlLabel>Invoice Start Date</ControlLabel>
							</Otherwise>
						</Choose>
						<CalendarDatePicker
							datePickerSettings={this.datePickerSettings}
							isClearable
							name="invoiceStartDate"
							value={invoiceStartDate}
							onChange={this.changeDate('invoiceStartDate')}
							onSelected={this.onDateSelected('invoiceStartDate')}
						/>
					</FormGroup>
				</Col>
				<Col md={3}>
					<FormGroup>
						<Choose>
							<When condition={(activePage === 'payment' && officerOrAgency === 'agency')}>
								<ControlLabel>End Date</ControlLabel>
							</When>
							<Otherwise>
								<ControlLabel>Invoice End Date</ControlLabel>
							</Otherwise>
						</Choose>
						<CalendarDatePicker
							datePickerSettings={this.datePickerSettings}
							isClearable
							name="invoiceEndDate"
							value={invoiceEndDate}
							onChange={this.changeDate('invoiceEndDate')}
							onSelected={this.onDateSelected('invoiceEndDate')}
							isValidDate={(current) => current.isSameOrAfter(invoiceStartDate)}
						/>
					</FormGroup>
				</Col>
			</Fragment>
		);

		return (
			<PaymentsContext.Consumer>
				{({
					activePage,
				}) => (
					<Fragment>
						<Row className="margin-bottom-10">
							<Col md={6}>
								<FormGroup>
									<ControlLabel>Client</ControlLabel>
									<Select
										value={clientId}
										name="clientId"
										options={clients}
										isLoading={loadingClients}
										onChange={this.onFilter('clientId')}
										isClearable
									/>
								</FormGroup>
							</Col>
							<If condition={activePage === 'payment' && officerOrAgency === 'agency'}>
								<Col md={6}>
									<FormGroup>
										<ControlLabel>Invoice Type</ControlLabel>
										<Select
											value={isEstimate}
											name="isEstimate"
											options={this.invoiceTypeFilters}
											onChange={this.onFilter('isEstimate')}
										/>
									</FormGroup>
								</Col>
							</If>
							<Choose>
								<When condition={activePage !== 'payment' || (activePage === 'payment' && officerOrAgency === 'officer')}>
									<Col md={6}>
										<FormGroup>
											<ControlLabel>Location</ControlLabel>
											<Select
												value={locationId}
												name="locationId"
												options={locations}
												isLoading={loadingLocations}
												onChange={this.onFilter('locationId')}
												isClearable
											/>
										</FormGroup>
									</Col>
								</When>
							</Choose>
						</Row>
						<Row>
							<Choose>
								<When condition={activePage !== 'payment' || (activePage === 'payment' && officerOrAgency === 'officer')}>
									<Col md={3}>
										<FormGroup>
											<ControlLabel>Job Start Date</ControlLabel>
											<CalendarDatePicker
												datePickerSettings={this.datePickerSettings}
												isClearable
												name="jobStartDate"
												value={jobStartDate}
												onChange={this.changeDate('jobStartDate')}
												onSelected={this.onDateSelected('jobStartDate')}
											/>
										</FormGroup>
									</Col>
									<Col md={3}>
										<FormGroup>
											<ControlLabel>Job End Date</ControlLabel>
											<CalendarDatePicker
												datePickerSettings={this.datePickerSettings}
												isClearable
												name="jobEndDate"
												value={jobEndDate}
												onChange={this.changeDate('jobEndDate')}
												onSelected={this.onDateSelected('jobEndDate')}
												isValidDate={(current) => current.isSameOrAfter(jobStartDate)}
											/>
										</FormGroup>
									</Col>
								</When>
								<Otherwise>
									{renderInvoideDateRange(activePage)}
								</Otherwise>
							</Choose>
							<If condition={activePage !== 'payment' || (activePage === 'payment' && officerOrAgency === 'officer')}>
								{renderInvoideDateRange(activePage)}
							</If>
						</Row>
						<If condition={activePage !== 'payment' || (activePage === 'payment' && officerOrAgency === 'officer')}>
							<Row>
								<Col md={2}>
									<Checkbox
										checked={prePay !== null}
										onChange={this.onFilter('prePay')}
									>
										Prepaid
									</Checkbox>
								</Col>
							</Row>
						</If>
					</Fragment>
				)}
			</PaymentsContext.Consumer>
		);
	}
}

const mapStateToProps = ({
	app : { user },
	payments : {
		loading : {
			listData  : loadingInvoices,
			counts    : loadingCounts,
			locations : loadingLocations,
			clients   : loadingClients,
		},
		data    : {
			locations,
			clients,
		},
		filters : {
			clientId,
			locationId,
			jobStartDate,
			jobEndDate,
			invoiceStartDate,
			invoiceEndDate,
			isEstimate,
			prePay,
		},
		officerOrAgency,
	},
}) => ({
	user,
	loading : {
		loadingInvoices,
		loadingCounts,
		loadingLocations,
		loadingClients,
	},
	filters : {
		clientId,
		locationId,
		jobStartDate,
		jobEndDate,
		invoiceStartDate,
		invoiceEndDate,
		isEstimate,
		prePay,
	},
	data : {
		locations,
		clients,
	},
	officerOrAgency,
});

const mapDispatchToProps = (dispatch) => ({
	...pick(bindActionCreators(thunks, dispatch), [
		'applyCriteria',
		'processUrl',
	]),
	...pick(bindActionCreators(Actions, dispatch), ['updateFilters']),
});

Filters.propTypes = {
	applyCriteria   : PropTypes.func.isRequired,
	filters         : PropTypes.object.isRequired,
	updateFilters   : PropTypes.func.isRequired,
	loading         : PropTypes.object.isRequired,
	data            : PropTypes.object.isRequired,
	officerOrAgency : PropTypes.string.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Filters);
